import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import Button from "ui/Button";
import SplitButton from "ui/SplitButton";
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import StorefrontIcon from '@mui/icons-material/Storefront';
import EventBusyIcon from '@mui/icons-material/EventBusy';

import DataSelectionTable from "components/DataTable/DataSelectionTable";
import NewTicketModal from "components/TicketsList/NewTicketModal.js";

import Content from "./Modal/Content";
import Actions from "./Modal/Actions";
import MerchFilter from "./MerchFilter";

import useScanRedeem from "components/ScanRedeem/useScanRedeem";
import useEndSalesController from "utils/useEndSalesController";
import { merchCollectionsColumns } from "utils/tables/columns/merchCollectionsColumns";
import usePaginationAndFilteringController from "utils/usePaginationAndFilteringController";
import fetchScannable from "utils/fetchScannable";

// Staff "Merch collection / pickup" surface — the non-ticket counterpart of
// TicketsList. It shares the scan → redeem core (useScanRedeem) but carries none
// of the ticket-only shell (selling, ending sales, stats). Collection is
// event-level, so resourceParams never include a session_id.
const MerchCollectionsList = ({ Resource, resourceParams, products = [], endableProducts = [], event, TicketResource, ticketResourceParams }) => {

  const [openNewSaleModal, setOpenNewSaleModal] = useState(false);

  const {
    objectState,
    fetchData,
    handleQueryChange,
    handleFilterChange,
    handlePageChange,
  } = usePaginationAndFilteringController({
    fetchDataResource: Resource,
    fetchDataParams: resourceParams,
  });

  useEffect(() => {
    fetchData();
  }, [resourceParams]);

  const { openScanner, openDetailsByUuid, renderScannerAndModal } = useScanRedeem({
    Resource,
    resourceParams,
    fetchItem: (params) => fetchScannable({ ...params, resourcePath: "merch_collections" }),
    forwardIntent: "collect",
    allowReverse: true,
    allowFastTrack: false,
    scanTitle: "Collect Merch",
    toastFor: (intent, amount) => {
      if (intent === "uncollect") return "Marked as not collected";
      return amount > 1 ? `Collected ${amount} items successfully` : "Collected successfully";
    },
    buildModalContent: (item, handleAction) => ({
      title: <strong>{item.title}</strong>,
      content: <Content item={item} />,
      button: (
        <Actions
          uuid={item.uuid}
          redeemState={item.redeem_state}
          orderAmount={item.order_amount}
          orderItems={item.order_items}
          onClick={handleAction}
        />
      ),
      fullScreen: true,
    }),
    onDone: fetchData,
  });

  // Ending merch sales reuses the ticket end-sales modal with an explicit item
  // list. Hidden for recurring events, whose merch stock is shared across the
  // parent-event window rather than per session.
  const {
    openEndSalesModal,
    EndSalesModalComponent,
    handleEndSalesModalOpen,
  } = useEndSalesController(event, TicketResource, ticketResourceParams, {
    items: endableProducts,
    titlePrefix: "End Merch Sales",
    itemNoun: "item",
  });

  const canEndMerchSales = Boolean(event?.single) && endableProducts.length > 0;

  const CashSaleButton = () =>
    <Button secondary onClick={() => setOpenNewSaleModal(true)} fullwidth icon={StorefrontIcon}>
      Register Cash Sale
    </Button>;

  const endMerchSalesOptions = [
    { label: "End Merch Sales", onClick: () => handleEndSalesModalOpen(), icon: <EventBusyIcon /> },
  ];

  return (
    <>
      <div className="tickets-tab-header">
        <Button highlighted onClick={openScanner} fullwidth icon={QrCodeScannerIcon}>
          Collect Merch
        </Button>
        {TicketResource && (
          canEndMerchSales
            ? <SplitButton MainButton={CashSaleButton} options={endMerchSalesOptions} secondary fullwidth={false} />
            : <CashSaleButton />
        )}
      </div>

      <DataSelectionTable
        records={objectState.filteredObjects}
        columns={merchCollectionsColumns((row) => openDetailsByUuid(row.uuid))}
        searchLabel="Search by Item reference, Order or Member"
        onQueryChange={handleQueryChange}
        queryString={objectState.query}
        serverPaginationProps={{
          paginationServer: true,
          paginationTotalRows: objectState.totalCount,
          page: objectState.page,
          onChangePage: handlePageChange,
        }}

        filter={
          <MerchFilter
            onChange={handleFilterChange}
          />
        }
      />

      {renderScannerAndModal()}

      {openNewSaleModal && (
        <NewTicketModal
          tickets={products}
          open={openNewSaleModal}
          onClose={() => setOpenNewSaleModal(false)}
          done={fetchData}
          TicketResource={TicketResource}
          ticketResourceParams={ticketResourceParams}
          itemNoun="Item"
          checkedInLabel="Mark as collected"
        />
      )}

      {openEndSalesModal && <EndSalesModalComponent />}
    </>
  );
};

MerchCollectionsList.propTypes = {
  Resource: PropTypes.object,
  resourceParams: PropTypes.object,
  products: PropTypes.array,
  endableProducts: PropTypes.array,
  event: PropTypes.object,
  TicketResource: PropTypes.object,
  ticketResourceParams: PropTypes.object,
};

export default MerchCollectionsList;
